import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Menu, X, User as UserIcon, Tag, Users, Power, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Logo } from '@/components/Logo'
import { useAuth } from '@/contexts/authContext'
import { authService } from '@/services/auth'
import { useUser } from '@/hooks/useUser'
import { queryKeys } from '@/lib/query-keys'
import { subscriptionService } from '@/services/subscription'
import { ROUTES } from '@/constants/routes'
import { CUSTOMER_TYPE, IndividualType } from '@/constants/account'
import { BILLING_PORTAL_LOCALE } from '@/constants/subscription'

export function Header() {
  const NAVIGATION_ITEMS = [
    { labelKey: 'header.navigation.discover', path: ROUTES.DISCOVER },
    { labelKey: 'header.navigation.faq', path: ROUTES.FAQ },
  ] as const

  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isUserMenuOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current?.contains(e.target as Node)) return
      setIsUserMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isUserMenuOpen])

  const queryClient = useQueryClient()
  const { isAuthenticated, refreshAuth } = useAuth()
  const { data: user } = useUser()
  const { i18n } = useTranslation()

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
    if (isUserMenuOpen) setIsUserMenuOpen(false)
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
    setIsUserMenuOpen(false)
  }

  const handleLogout = () => {
    authService.clearTokens()
    queryClient.removeQueries({ queryKey: queryKeys.users.me })
    refreshAuth()
    setIsUserMenuOpen(false)
    navigate(ROUTES.LOGIN, { replace: true })
  }

  const handleGoToAccount = () => {
    setIsUserMenuOpen(false)
    navigate(ROUTES.DASHBOARD)
  }

  const handleGoToSubscription = async () => {
    if (!user || isSubscriptionLoading) return
    setIsUserMenuOpen(false)
    setIsSubscriptionLoading(true)
    try {
      const customerType =
        user.customer_type === CUSTOMER_TYPE.ORGANIZATION
          ? CUSTOMER_TYPE.ORGANIZATION
          : CUSTOMER_TYPE.USER
      const returnUrl = `${window.location.origin}${ROUTES.DASHBOARD}`
      const locale = i18n.language?.startsWith('en')
        ? BILLING_PORTAL_LOCALE.EN
        : BILLING_PORTAL_LOCALE.FR
      const organizationId =
        customerType === CUSTOMER_TYPE.ORGANIZATION
          ? (user.organization_quote?.organization_id ??
            user.subscriptions?.[0]?.organization_id ??
            null)
          : undefined

      const { url } = await subscriptionService.createBillingPortalSession({
        customer_type: customerType,
        organization_id: organizationId,
        return_url: returnUrl,
        locale,
      })
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch {
      navigate(ROUTES.SUBSCRIBE)
    } finally {
      setIsSubscriptionLoading(false)
    }
  }

  const handleGoToSeats = () => {
    setIsUserMenuOpen(false)
    navigate(ROUTES.SEATS)
  }

  const isOrganization = user?.customer_type === CUSTOMER_TYPE.ORGANIZATION
  const hasPaidSubscription = (user?.subscriptions?.length ?? 0) > 0
  const isQuoteRequesterWithActiveLicense =
    (user?.organization_quote != null && user?.license_activated === true) ?? false
  const showSeatsMenu = isOrganization && (hasPaidSubscription || isQuoteRequesterWithActiveLicense)

  const getUserRoleLabel = () => {
    if (!user) return ''
    switch (user.individual_type) {
      case IndividualType.INTERN:
        return t('header.roles.intern')
      case IndividualType.DOCTOR:
        return t('header.roles.doctor')
      case IndividualType.ADHERENT:
        return t('header.roles.adherent')
      default:
        return ''
    }
  }

  const fullName =
    user && (user.last_name || user.first_name)
      ? `${user.last_name ?? ''} ${user.first_name ?? ''}`.trim()
      : user?.username

  return (
    <header className="bg-background border-border w-full border-b py-4">
      <div className="mx-auto flex max-w-360 items-center justify-between px-4">
        <Link
          to={isAuthenticated ? ROUTES.DASHBOARD : ROUTES.HOME}
          className="flex items-center"
          onClick={closeMobileMenu}
        >
          <Logo variant="dark" />
        </Link>

        <nav className="hidden items-center gap-4 md:flex">
          {NAVIGATION_ITEMS.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className="text-foreground text-base font-normal transition-opacity hover:opacity-80"
            >
              {t(item.labelKey)}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-6 md:flex">
          {!isAuthenticated || !user ? (
            <>
              <Link
                to="/login"
                className="text-foreground text-base font-medium transition-opacity hover:opacity-80"
              >
                {t('header.buttons.login')}
              </Link>
              <Link
                to="/subscribe"
                className="bg-primary text-primary-foreground rounded-md px-10 py-2.5 text-base font-medium transition-opacity hover:opacity-90"
              >
                {t('header.buttons.subscribe')}
              </Link>
            </>
          ) : (
            <div className="relative" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => setIsUserMenuOpen(open => !open)}
                className="hover:bg-button-hover flex items-center gap-3 rounded-full px-2 py-1.5 transition-colors"
                aria-expanded={isUserMenuOpen}
                aria-haspopup="true"
              >
                <img
                  src={isOrganization ? '/struct.svg' : '/user.png'}
                  alt=""
                  className="h-[33px] w-[33px] shrink-0 object-contain"
                />
                <div className="flex flex-col items-start">
                  <span className="text-heading text-sm font-semibold">{fullName}</span>
                  <span className="text-label text-xs">{getUserRoleLabel()}</span>
                </div>
                <ChevronDown
                  className={`text-label h-5 w-5 shrink-0 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isUserMenuOpen && (
                <div className="border-border bg-card absolute right-0 mt-2 w-48 rounded-xl border text-sm shadow-lg">
                  <button
                    type="button"
                    onClick={handleGoToAccount}
                    className="hover:bg-button-hover text-heading flex w-full items-center gap-3 px-4 py-2 text-left"
                  >
                    <UserIcon className="text-neutral-gray h-4 w-4" />
                    <span>{t('header.userMenu.account')}</span>
                  </button>
                  {hasPaidSubscription && (
                    <button
                      type="button"
                      onClick={handleGoToSubscription}
                      disabled={isSubscriptionLoading}
                      className="hover:bg-button-hover text-heading flex w-full items-center gap-3 px-4 py-2 text-left disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Tag className="text-neutral-gray h-4 w-4" />
                      <span>
                        {isSubscriptionLoading
                          ? t('header.userMenu.subscriptionLoading')
                          : t('header.userMenu.subscription')}
                      </span>
                    </button>
                  )}
                  {showSeatsMenu && (
                    <button
                      type="button"
                      onClick={handleGoToSeats}
                      className="hover:bg-button-hover text-heading flex w-full items-center gap-3 px-4 py-2 text-left"
                    >
                      <Users className="text-neutral-gray h-4 w-4" />
                      <span>{t('header.userMenu.seats')}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="hover:bg-destructive-light text-destructive flex w-full items-center gap-3 px-4 py-2 text-left"
                  >
                    <Power className="h-4 w-4" />
                    <span>{t('header.userMenu.logout')}</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={toggleMobileMenu}
          className="text-foreground md:hidden"
          aria-label={t('header.mobile.toggleMenu')}
        >
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={closeMobileMenu}
            aria-hidden="true"
          />
          <div className="border-border bg-background fixed inset-y-0 right-0 z-50 w-full border-l md:hidden">
            <div className="flex h-full flex-col px-4 py-6">
              <div className="mb-8 flex justify-end">
                <button
                  type="button"
                  onClick={closeMobileMenu}
                  className="text-foreground"
                  aria-label={t('header.mobile.closeMenu')}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <nav className="flex flex-1 flex-col gap-6">
                {NAVIGATION_ITEMS.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="text-foreground text-lg font-normal transition-opacity hover:opacity-80"
                    onClick={closeMobileMenu}
                  >
                    {t(item.labelKey)}
                  </Link>
                ))}
                {!isAuthenticated || !user ? (
                  <>
                    <Link
                      to="/login"
                      className="text-foreground text-lg font-medium transition-opacity hover:opacity-80"
                      onClick={closeMobileMenu}
                    >
                      {t('header.buttons.login')}
                    </Link>
                    <Link
                      to="/subscribe"
                      className="bg-primary text-primary-foreground rounded-md px-10 py-2.5 text-center text-lg font-medium transition-opacity hover:opacity-90"
                      onClick={closeMobileMenu}
                    >
                      {t('header.buttons.subscribe')}
                    </Link>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        handleGoToAccount()
                        closeMobileMenu()
                      }}
                      className="hover:bg-button-hover text-heading flex items-center gap-3 rounded-lg px-4 py-2 text-left text-lg font-medium"
                    >
                      <UserIcon className="text-neutral-gray h-5 w-5" />
                      <span>{t('header.userMenu.account')}</span>
                    </button>
                    {hasPaidSubscription && (
                      <button
                        type="button"
                        onClick={() => {
                          handleGoToSubscription()
                          closeMobileMenu()
                        }}
                        disabled={isSubscriptionLoading}
                        className="hover:bg-button-hover text-heading flex items-center gap-3 rounded-lg px-4 py-2 text-left text-lg font-medium disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <Tag className="text-neutral-gray h-5 w-5" />
                        <span>
                          {isSubscriptionLoading
                            ? t('header.userMenu.subscriptionLoading')
                            : t('header.userMenu.subscription')}
                        </span>
                      </button>
                    )}
                    {showSeatsMenu && (
                      <button
                        type="button"
                        onClick={() => {
                          handleGoToSeats()
                          closeMobileMenu()
                        }}
                        className="hover:bg-button-hover text-heading flex items-center gap-3 rounded-lg px-4 py-2 text-left text-lg font-medium"
                      >
                        <Users className="text-neutral-gray h-5 w-5" />
                        <span>{t('header.userMenu.seats')}</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        handleLogout()
                        closeMobileMenu()
                      }}
                      className="hover:bg-destructive-light text-destructive flex items-center gap-3 rounded-lg px-4 py-2 text-left text-lg font-medium"
                    >
                      <Power className="h-5 w-5" />
                      <span>{t('header.userMenu.logout')}</span>
                    </button>
                  </>
                )}
              </nav>
            </div>
          </div>
        </>
      )}
    </header>
  )
}
